import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { authHeaders } from '../utils/auth';

const DEFAULT_LAYOUT = [
    { i: 'controls', x: 0, y: 0, w: 2, h: 10 },
    { i: 'stream', x: 2, y: 0, w: 10, h: 10 }
];

interface LogEntry {
    ts: string;
    level: string;
    component: string;
    message: string;
}

const LEVEL_COLORS: Record<string, string> = {
    ERROR: 'text-red-400',
    WARN: 'text-yellow-400',
    WARNING: 'text-yellow-400',
    SUCCESS: 'text-green-400',
    INFO: 'text-slate-300',
    DEBUG: 'text-slate-500',
};

export const LiveLogsPanel: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [rawLogs, setRawLogs] = useState<string[]>([]);
    const [autoScroll, setAutoScroll] = useState(true);
    const [filter, setFilter] = useState('ALL');
    const [wsStatus, setWsStatus] = useState<'connecting' | 'live' | 'polling' | 'offline'>('connecting');
    const [logCount, setLogCount] = useState(0);
    const bottomRef = useRef<HTMLDivElement>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const pollRef = useRef<NodeJS.Timeout | null>(null);

    // Draggable Layout State
    const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
        const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_logs_layout');
        if (saved) {
            try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_logs_layout', JSON.stringify(allLayouts));
    };

    const appendLog = useCallback((entry: LogEntry) => {
        setLogs(prev => {
            const updated = [...prev, entry].slice(-500);
            setLogCount(updated.length);
            return updated;
        });
    }, []);

    const appendLogs = useCallback((entries: LogEntry[]) => {
        setLogs(prev => {
            const updated = [...prev, ...entries].slice(-500);
            setLogCount(updated.length);
            return updated;
        });
    }, []);

    // ── WebSocket connection (primary) ────────────────────────────────────────
    const connectWS = useCallback(() => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        const wsUrl = `${protocol}//${host}/ws/logs`;

        setWsStatus('connecting');
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
            setWsStatus('live');
            // Stop polling if it was running as fallback
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            appendLog({
                ts: new Date().toISOString(),
                level: 'INFO',
                component: 'ws-client',
                message: '⚡ WebSocket live stream connected',
            });
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (msg.type === 'backlog' && Array.isArray(msg.data)) {
                    appendLogs(msg.data);
                } else if (msg.type === 'log' && msg.data) {
                    appendLog(msg.data);
                }
                // ignore ping frames
            } catch { }
        };

        ws.onclose = () => {
            wsRef.current = null;
            setWsStatus('polling');
            appendLog({
                ts: new Date().toISOString(),
                level: 'WARN',
                component: 'ws-client',
                message: '⚠️ WebSocket closed — fallback to polling (5s)',
            });
            startPollingFallback();
            // Reconnect after 10s
            setTimeout(connectWS, 10000);
        };

        ws.onerror = () => {
            setWsStatus('polling');
            ws.close();
        };
    }, [appendLog, appendLogs]);

    // ── Polling fallback (if WS unavailable) ─────────────────────────────────
    const startPollingFallback = useCallback(() => {
        if (pollRef.current) return;

        const fetchLogs = async () => {
            try {
                const res = await fetch('/v1/mcp/omni-bridge/logs?limit=50', { headers: authHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    const bridgeLogs: LogEntry[] = (data.logs || []).map((l: any) => ({
                        ts: l.ts || new Date().toISOString(),
                        level: l.status === 'error' ? 'ERROR' : (l.status === 'success' ? 'SUCCESS' : 'INFO'),
                        component: l.agent ? `${l.agent}${l.skill ? `->${l.skill}` : ''}` : 'bridge',
                        message: l.error ? `Error: ${l.error}` : `Route: ${l.route || '?'} (${l.latency_ms || 0}ms)`,
                    }));
                    if (bridgeLogs.length > 0) appendLogs(bridgeLogs);
                }
            } catch { }
        };

        fetchLogs();
        pollRef.current = setInterval(fetchLogs, 5000);
    }, [appendLogs]);

    // ── Bootstrap ────────────────────────────────────────────────────────────
    useEffect(() => {
        connectWS();
        return () => {
            wsRef.current?.close();
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    // ── Auto-scroll ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (autoScroll && bottomRef.current) {
            (() => { const el = bottomRef.current; if(el && el.parentElement) { el.parentElement.scrollTop = el.parentElement.scrollHeight; } })()
        }
    }, [logs, autoScroll]);

    // ── Download logs ────────────────────────────────────────────────────────
    const handleDownload = () => {
        const text = logs.map(l => `[${l.ts}] [${l.level}] [${l.component}] ${l.message}`).join('\n');
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `omnigate-logs-${Date.now()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ── Filter ───────────────────────────────────────────────────────────────
    const filteredLogs = logs.filter(l => {
        if (filter === 'ALL') return true;
        return l.level === filter || l.level === filter + 'ING';
    });

    const statusBadge = {
        connecting: { label: 'CONNECTING', cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30' },
        live: { label: '⚡ LIVE', cls: 'text-green-400 bg-green-500/10 border-green-500/30' },
        polling: { label: '⏱ POLLING', cls: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
        offline: { label: 'OFFLINE', cls: 'text-red-400 bg-red-500/10 border-red-500/30' },
    }[wsStatus];

    if (!mounted) return null;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(56, 189, 248, 0.2); border: 2px dashed rgba(56, 189, 248, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                .drag-handle { cursor: grab; }
                .drag-handle:active { cursor: grabbing; }
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #0a0c10; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); font-family: monospace; }
                .panel-wrapper:hover { border-color: rgba(56, 189, 248, 0.3); }
            `}</style>

            <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
                <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_logs_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
                    <i className="fas fa-sync-alt mr-1"></i> Reset Layout
                </button>
            </div>

            
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
                className="layout flex-1"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={50}
                onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                {/* Panel 1: Controls & Filters */}
                <div key="controls" className="panel-wrapper bg-[#131722]">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#1e222d]/80 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-sliders-h text-sky-400"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Settings</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {/* WS Status badge */}
                        <div className="space-y-1">
                            <label className="text-[9px] font-bold text-slate-500 uppercase">Connection</label>
                            <div className={`text-[10px] font-bold px-3 py-1.5 rounded border text-center ${statusBadge.cls}`}>
                                {statusBadge.label}
                            </div>
                        </div>

                        {/* Log Stats */}
                        <div className="space-y-1">
                            <label className="text-[9px] font-bold text-slate-500 uppercase">Buffer Size</label>
                            <div className="text-[14px] text-slate-300 font-bold bg-[#0a0c10] border border-[#2a2e39] rounded px-3 py-2 text-center">
                                {logCount}
                            </div>
                        </div>

                        {/* Level filters */}
                        <div className="space-y-2">
                            <label className="text-[9px] font-bold text-slate-500 uppercase">Log Level</label>
                            <div className="flex flex-col gap-1.5">
                                {['ALL', 'INFO', 'WARN', 'ERROR', 'DEBUG'].map(f => (
                                    <button
                                        key={f}
                                        onClick={() => setFilter(f)}
                                        className={`w-full text-left px-3 py-1.5 rounded transition-all flex items-center justify-between 
                                            ${filter === f ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30 font-bold' : 'bg-[#0a0c10] text-slate-500 border border-[#2a2e39] hover:border-slate-600'} text-[10px]`}
                                    >
                                        <span>{f}</span>
                                        {filter === f && <i className="fas fa-check text-sky-500 text-[10px]"></i>}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="space-y-2 pt-2 border-t border-[#2a2e39]">
                            <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer p-2 bg-[#0a0c10] border border-[#2a2e39] rounded hover:border-slate-500 transition-colors">
                                <input type="checkbox" checked={autoScroll} onChange={e => setAutoScroll(e.target.checked)} className="accent-sky-500 w-3 h-3" />
                                <span className="font-bold">Auto-scroll</span>
                            </label>

                            <div className="grid grid-cols-2 gap-2 mt-2">
                                <button onClick={() => setLogs([])} className="text-[10px] bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors px-2 py-2 rounded flex flex-col items-center justify-center gap-1" title="Clear logs">
                                    <i className="fas fa-trash"></i> <span>Clear</span>
                                </button>
                                <button onClick={handleDownload} className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors px-2 py-2 rounded flex flex-col items-center justify-center gap-1" title="Download logs">
                                    <i className="fas fa-download"></i> <span>Export</span>
                                </button>
                            </div>

                            <button
                                onClick={() => { wsRef.current?.close(); connectWS(); }}
                                className="w-full mt-2 text-[10px] bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 transition-colors px-2 py-2 rounded flex items-center justify-center gap-2 font-bold"
                            >
                                <i className="fas fa-sync-alt"></i> Reconnect
                            </button>
                        </div>
                    </div>
                </div>

                {/* Panel 2: Log Stream */}
                <div key="stream" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#1e222d]/80 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xs font-black text-slate-300 uppercase tracking-widest flex items-center gap-2">
                                <i className="fas fa-terminal text-green-500"></i> Omni Engine Console
                            </h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="p-3 flex-1 overflow-y-auto text-[11px] leading-relaxed space-y-0.5 custom-scrollbar">
                        {filteredLogs.length === 0 ? (
                            <div className="text-slate-600 italic text-center py-8">
                                {wsStatus === 'connecting' ? 'Connecting to log stream...' : 'No logs matching filter.'}
                            </div>
                        ) : (
                            filteredLogs.map((log, i) => (
                                <div key={i} className={`flex gap-2 ${LEVEL_COLORS[log.level] || 'text-slate-400'}`}>
                                    <span className="text-slate-600 flex-shrink-0 w-20 truncate text-[10px]">
                                        {log.ts?.slice(11, 19) || '?'}
                                    </span>
                                    <span className={`flex-shrink-0 w-14 text-[9px] font-bold text-right ${LEVEL_COLORS[log.level] || ''}`}>
                                        {log.level}
                                    </span>
                                    <span className="flex-shrink-0 w-20 text-[9px] text-slate-500 truncate">
                                        [{log.component}]
                                    </span>
                                    <span className="flex-1 break-all">{log.message}</span>
                                </div>
                            ))
                        )}
                        <div ref={bottomRef} />
                    </div>
                </div>
            </ResponsiveGridLayout>
        </div>
    );
};
